/* SPF redesign — product page (variant picker, gallery, quantity, AJAX add to cart),
   product card add to cart and "Kombinera med" recommendations. No dependencies.
   Cart updates go through the Flow theme's own event bus so its drawer and counter stay in sync. */
(() => {
  /* Several sections include this file; only the first copy may bind document-level handlers. */
  if (window.spfProductLoaded) return;
  window.spfProductLoaded = true;

  const ADDED_MS = 1800;
  const FALLBACK_ERROR = 'Det gick inte att lägga produkten i kundvagnen. Försök igen.';

  const pageContainer = () => document.getElementById('PageContainer');
  const usesCartDrawer = () => pageContainer()?.dataset.cartType === 'drawer';
  const routeUrl = (key, fallback) => (window.routes && window.routes[key]) || fallback;

  /* Shopify money format, e.g. "{{amount_with_comma_separator}} kr". trimZeros mirrors money_without_trailing_zeros. */
  const MONEY_SEPARATORS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };

  function formatMoney(cents, format, trimZeros) {
    const template = format || '{{amount}} kr';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const key = (template.match(placeholder) || [])[1];
    const [defaultDecimals, thousands, decimal] = MONEY_SEPARATORS[key] || MONEY_SEPARATORS.amount;
    const decimals = trimZeros && cents % 100 === 0 ? 0 : defaultDecimals;
    const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return template.replace(placeholder, fraction ? grouped + decimal + fraction : grouped);
  }

  async function addToCart(items, sectionId) {
    const response = await fetch(routeUrl('cart_add_url', '/cart/add') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: 'cart-drawer' })
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = [json.description, json.message].find((text) => typeof text === 'string' && text);
      throw new Error(message || FALLBACK_ERROR);
    }

    const bus = window.eventBus;
    if (bus) {
      bus.emit('cart:added', { sectionId });
      bus.emit('update:cart:drawer', json);
    }
    const cart = await fetch(routeUrl('cart_url', '/cart') + '.js', { credentials: 'same-origin' })
      .then((res) => res.json())
      .catch(() => null);
    if (cart && typeof window.wetheme?.updateCartCount === 'function') {
      window.wetheme.updateCartCount(cart);
    }
    if (bus && pageContainer()?.dataset.cartAction === 'go_to_or_open_cart') {
      bus.emit('open:cart:drawer', { scrollToTop: true });
    }
    return json;
  }

  /* Shared submit handling: AJAX only when Flow uses the cart drawer, otherwise the form posts normally. */
  async function submitAddForm(event, { button, status, items, sectionId, onAdded, onDone }) {
    if (!usesCartDrawer()) return;
    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      await addToCart(items, sectionId);
      onAdded();
      window.setTimeout(onDone, ADDED_MS);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  class SpfProduct {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.moneyFormat = root.dataset.moneyFormat;
      this.variants = JSON.parse(root.querySelector('[data-spf-variants]')?.textContent || '[]');
      this.form = root.querySelector('[data-spf-form]');
      this.idInput = root.querySelector('[data-spf-variant-id]');
      this.qtyInput = root.querySelector('[data-spf-qty]');
      this.addButton = root.querySelector('[data-spf-add]');
      this.addLabel = root.querySelector('[data-spf-add-label]');
      this.status = root.querySelector('[data-spf-status]');
      this.optionGroups = [...root.querySelectorAll('[data-spf-option]')];
      this.shortcuts = [...root.querySelectorAll('[data-spf-qty-shortcut]')];
      this.added = false;

      const initialId = Number(this.idInput?.value);
      this.variant = this.variants.find((variant) => variant.id === initialId) || this.variants[0];

      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindForm();
      this.bindTabs();
      this.renderProof();
      this.renderAddButton();
      this.watchLayout();
      this.rememberProduct();
    }

    bindGallery() {
      this.root.querySelectorAll('[data-spf-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
      });
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const id = String(mediaId);
      const slides = [...this.root.querySelectorAll('[data-spf-slide]')];
      if (!slides.some((slide) => slide.dataset.mediaId === id)) return;
      slides.forEach((slide) => {
        slide.hidden = slide.dataset.mediaId !== id;
      });
      this.root.querySelectorAll('[data-spf-thumb]').forEach((thumb) => {
        if (thumb.dataset.mediaId === id) {
          thumb.setAttribute('aria-current', 'true');
          /* Keep the active thumbnail in view without scrolling the page itself. */
          const row = thumb.parentElement;
          row.scrollTo({ left: thumb.offsetLeft - (row.clientWidth - thumb.clientWidth) / 2 });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    bindOptions() {
      this.optionGroups.forEach((group) => {
        const index = Number(group.dataset.index);
        group.querySelectorAll('[data-spf-value]').forEach((button) => {
          button.addEventListener('click', () => this.selectOption(index, button.dataset.spfValue));
        });
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => options.every((value, i) => variant.options[i] === value));
    }

    selectOption(index, value) {
      if (!this.variant) return;
      const wanted = this.variant.options.slice();
      wanted[index] = value;
      const withValue = this.variants.filter((variant) => variant.options[index] === value);
      const next = this.findVariant(wanted) || withValue.find((variant) => variant.available) || withValue[0];
      if (next) this.setVariant(next);
    }

    setVariant(variant) {
      this.variant = variant;
      if (this.idInput) this.idInput.value = variant.id;
      this.renderOptions();
      this.renderPrice();
      this.renderAddButton();
      this.renderStock();
      this.showMedia(variant.media_id);
      if (this.status) this.status.textContent = '';

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    renderOptions() {
      const selected = this.variant.options;
      this.optionGroups.forEach((group) => {
        const index = Number(group.dataset.index);
        const label = group.querySelector('[data-spf-option-selected]');
        if (label) label.textContent = selected[index];
        group.querySelectorAll('[data-spf-value]').forEach((button) => {
          const value = button.dataset.spfValue;
          const combination = selected.slice();
          combination[index] = value;
          const match = this.findVariant(combination);
          const unavailable = !match || !match.available;
          button.setAttribute('aria-pressed', String(value === selected[index]));
          button.classList.toggle('is-unavailable', unavailable);
          if (unavailable) {
            button.setAttribute('aria-label', `${value}, ${match ? 'slutsåld' : 'finns inte'}`);
          } else {
            button.removeAttribute('aria-label');
          }
        });
      });
    }

    renderPrice() {
      const { price, compare_at_price: compareAt } = this.variant;
      const priceEl = this.root.querySelector('[data-spf-price]');
      if (priceEl) priceEl.textContent = formatMoney(price, this.moneyFormat, true);

      const compare = this.root.querySelector('[data-spf-compare]');
      if (compare) {
        const onSale = compareAt > price;
        compare.hidden = !onSale;
        const value = compare.querySelector('[data-spf-compare-value]');
        if (onSale && value) value.textContent = formatMoney(compareAt, this.moneyFormat, true);
      }

      const save = this.root.querySelector('[data-spf-save]');
      if (save) {
        // Same rule as spf-product-card: per mille, half up, tag only from 4.5 % off.
        const permille = compareAt > price ? Math.floor(((compareAt - price) * 1000) / compareAt) : 0;
        save.hidden = permille < 45;
        const saveValue = save.querySelector('[data-spf-save-value]');
        if (saveValue) saveValue.textContent = String(Math.floor((permille + 5) / 10));
      }

      const perPortion = this.root.querySelector('[data-spf-per-portion]');
      const portions = Number(perPortion?.dataset.portions);
      if (perPortion && portions > 0) {
        perPortion.textContent = formatMoney(Math.round(price / portions), this.moneyFormat, false);
      }
    }

    renderStock() {
      const stock = this.root.querySelector('[data-spf-stock]');
      const state = this.variant?.stock;
      if (!stock || !state) return;
      stock.dataset.state = state;
      const text = stock.querySelector('[data-spf-stock-text]');
      if (text) text.textContent = stock.getAttribute(`data-message-${state}`) || '';
    }

    quantity() {
      const value = parseInt(this.qtyInput?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    renderAddButton() {
      if (!this.addButton || !this.addLabel || this.added) return;
      const available = Boolean(this.variant?.available);
      this.addButton.disabled = !available;
      this.shortcuts.forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-pressed', String(Number(button.dataset.spfQtyShortcut) === this.quantity()));
      });
      if (!available) {
        this.addLabel.textContent = 'Slutsåld';
        return;
      }
      const quantity = this.quantity();
      const total = this.variant.price * quantity;
      const percent = this.quantityDiscount(quantity);
      const money = (cents) => formatMoney(cents, this.moneyFormat, true);
      // The price sits in its own span so a narrow button wraps before it, never inside it.
      const price = document.createElement('span');
      price.className = 'spf-product__add-price';
      if (percent) {
        // Mirrors the automatic quantity discount that the cart applies, so the price is honest before the click.
        const was = document.createElement('s');
        was.className = 'spf-product__add-was';
        was.textContent = money(total);
        const tag = document.createElement('span');
        tag.className = 'spf-product__add-tag';
        tag.textContent = `−${percent} %`;
        price.append(was, ' ', money(Math.round((total * (100 - percent)) / 100)), ' ', tag);
      } else {
        price.textContent = money(total);
      }
      this.addLabel.replaceChildren('Lägg i kundvagn', ' ', price);
    }

    /* Quantity tiles may carry data-spf-qty-discount (percent, applies from that quantity upwards). */
    quantityDiscount(quantity) {
      return this.shortcuts.reduce((best, button) => {
        const from = Number(button.dataset.spfQtyShortcut);
        const percent = Number(button.dataset.spfQtyDiscount);
        return percent > best && quantity >= from ? percent : best;
      }, 0);
    }

    bindQuantity() {
      if (!this.qtyInput) return;
      this.root.querySelectorAll('[data-spf-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          this.qtyInput.value = Math.max(1, this.quantity() + Number(button.dataset.spfQtyStep));
          this.renderAddButton();
        });
      });
      /* Quantity tiles ("1 st / 2 st / 4 st") set the same quantity as the stepper. */
      this.shortcuts.forEach((button) => {
        button.addEventListener('click', () => {
          this.qtyInput.value = Math.max(1, Number(button.dataset.spfQtyShortcut) || 1);
          this.renderAddButton();
        });
      });
      this.qtyInput.addEventListener('input', () => this.renderAddButton());
      this.qtyInput.addEventListener('change', () => {
        this.qtyInput.value = this.quantity();
        this.renderAddButton();
      });
    }

    /* "Omdömen i korthet": the latest Judge.me reviews that have text, whatever their score. */
    renderProof() {
      const proof = this.root.querySelector('[data-spf-proof]');
      const list = proof?.querySelector('[data-spf-proof-list]');
      if (!list) return;
      const limit = Number(proof.dataset.limit) || 3;
      const monthYear = new Intl.DateTimeFormat('sv-SE', { month: 'long', year: 'numeric' });
      const reviews = [...document.querySelectorAll('.jdgm-rev')]
        .map((review) => {
          const text = (s) => review.querySelector(s)?.textContent.trim() || '';
          const stamp = review.querySelector('.jdgm-rev__timestamp')?.dataset.content || '';
          const date = new Date(stamp.replace(' UTC', 'Z').replace(' ', 'T'));
          return {
            quote: text('.jdgm-rev__body') || text('.jdgm-rev__title'),
            author: text('.jdgm-rev__author'),
            score: Number(review.querySelector('.jdgm-rev__rating')?.dataset.score),
            verified: review.dataset.verifiedBuyer === 'true',
            time: date.getTime()
          };
        })
        .filter((review) => review.quote && review.score >= 1 && review.score <= 5 && Number.isFinite(review.time))
        .sort((a, b) => b.time - a.time)
        .slice(0, limit);
      if (!reviews.length) return;

      const element = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text) node.textContent = text;
        return node;
      };
      reviews.forEach((review) => {
        const item = element('li', 'spf-product__proof-item');
        const stars = element('span', 'spf-stars', '★'.repeat(review.score));
        stars.setAttribute('aria-hidden', 'true');
        if (review.score < 5) stars.append(element('span', 'spf-product__star-empty', '★'.repeat(5 - review.score)));
        const meta = [review.author, review.verified ? 'Verifierat köp' : '', monthYear.format(review.time)];
        item.append(
          stars,
          element('span', 'spf-sr-only', `${review.score} av 5 stjärnor`),
          element('blockquote', 'spf-product__proof-quote', review.quote),
          element('p', 'spf-product__proof-meta', meta.filter(Boolean).join(' · '))
        );
        list.append(item);
      });
      list.hidden = false;
      if (reviews.length > 1) this.renderProofDots(proof, list);
    }

    /* Narrow screens show one quote at a time (spf-product.css): dots say which one and jump on click. */
    renderProofDots(proof, list) {
      const items = [...list.children];
      const dots = document.createElement('div');
      dots.className = 'spf-product__proof-dots';
      items.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'spf-product__proof-dot';
        dot.setAttribute('aria-label', `Omdöme ${index + 1} av ${items.length}`);
        dot.addEventListener('click', () => list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' }));
        dots.append(dot);
      });
      const update = () => {
        const width = items[0].offsetWidth || 1;
        const current = Math.min(items.length - 1, Math.round(list.scrollLeft / width));
        [...dots.children].forEach((dot, index) => {
          if (index === current) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      update();
      list.addEventListener('scroll', update, { passive: true });
      proof.append(dots);
    }

    bindTabs() {
      const group = this.root.querySelector('[data-spf-tabs]');
      if (!group) return;
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      const panelOf = (control) => document.getElementById(control.getAttribute('aria-controls'));
      const select = (tab, focus) => {
        tabs.forEach((other) => {
          const selected = other === tab;
          other.setAttribute('aria-selected', String(selected));
          other.tabIndex = selected ? 0 : -1;
          panelOf(other).hidden = !selected;
        });
        if (focus) tab.focus();
      };
      // Narrow screens: the tab list is hidden and the toggle headings run the same panels as an
      // accordion, all collapsed to begin with. Switching width re-applies whichever mode fits.
      const narrow = window.matchMedia('(max-width: 640px)');
      const toggles = [...group.querySelectorAll('[data-spf-toggle]')];
      const expand = (toggle, open) => {
        toggle.setAttribute('aria-expanded', String(open));
        panelOf(toggle).hidden = !open;
      };
      const applyMode = () => {
        if (narrow.matches) toggles.forEach((toggle) => expand(toggle, false));
        else select(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0], false);
      };
      toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => expand(toggle, toggle.getAttribute('aria-expanded') !== 'true'));
      });
      applyMode();
      narrow.addEventListener('change', applyMode);
      const open = (id) => {
        const tab = tabs.find((candidate) => candidate.getAttribute('aria-controls') === id);
        if (!tab) return false;
        if (narrow.matches) expand(toggles.find((toggle) => toggle.getAttribute('aria-controls') === id), true);
        else select(tab, false);
        return true;
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (event) => {
          const last = tabs.length - 1;
          const target = {
            ArrowRight: index === last ? 0 : index + 1,
            ArrowLeft: index === 0 ? last : index - 1,
            Home: 0,
            End: last,
          }[event.key];
          if (target === undefined) return;
          event.preventDefault();
          select(tabs[target], true);
        });
      });
      // Theme editor: selecting a block in the sidebar opens its tab.
      group.addEventListener('shopify:block:select', (event) => {
        if (tabs.includes(event.target)) open(event.target.getAttribute('aria-controls'));
      });
      // In-page links such as "28 omdömen" point at a panel: open it before the browser scrolls there.
      const openFromHash = () => {
        const id = decodeURIComponent(location.hash.slice(1));
        if (!id || !open(id)) return;
        // The panel was hidden when the browser tried to jump to it, so bring it into view now.
        (narrow.matches ? document.getElementById(id) : group).scrollIntoView({ block: 'start' });
      };
      openFromHash();
      window.addEventListener('hashchange', openFromHash);
    }

    bindForm() {
      if (!this.form || !this.addButton) return;
      this.form.addEventListener('submit', (event) => {
        submitAddForm(event, {
          button: this.addButton,
          status: this.status,
          items: [{ id: this.variant.id, quantity: this.quantity() }],
          sectionId: this.sectionId,
          onAdded: () => {
            this.added = true;
            if (this.addLabel) this.addLabel.textContent = 'Tillagd i kundvagnen';
          },
          onDone: () => {
            this.added = false;
            this.renderAddButton();
          }
        });
      });
    }

    /* The gallery is sticky only while the two columns sit side by side. */
    watchLayout() {
      const grid = this.root.querySelector('[data-spf-grid]');
      const media = this.root.querySelector('[data-spf-media]');
      const info = media?.nextElementSibling;
      if (!grid || !media || !info) return;
      const update = () => {
        const sideBySide = info.getBoundingClientRect().left >= media.getBoundingClientRect().right;
        grid.dataset.spfLayout = sideBySide ? 'columns' : 'stacked';
      };
      update();
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(grid);
    }

    /* Keeps Flow's "recently viewed" list working, as the replaced product section did. */
    rememberProduct() {
      const id = Number(this.root.dataset.productId);
      if (!id) return;
      try {
        const key = 'theme:recently-viewed-products';
        const list = JSON.parse(localStorage.getItem(key) || '[]').filter((item) => item !== id);
        list.unshift(id);
        localStorage.setItem(key, JSON.stringify(list));
      } catch (error) {
        /* Storage can be unavailable (private mode, blocked site data); the list is optional. */
      }
    }
  }

  function initCardForms() {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest?.('[data-spf-card-form]');
      if (!form) return;
      const button = form.querySelector('[data-spf-card-add]');
      const label = form.querySelector('[data-spf-add-label]');
      const idInput = form.querySelector('input[name="id"]');
      if (!button || !idInput) return;
      const original = label?.textContent;
      submitAddForm(event, {
        button,
        status: form.querySelector('[data-spf-status]'),
        items: [{ id: Number(idInput.value), quantity: 1 }],
        sectionId: form.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', ''),
        onAdded: () => {
          if (label) label.textContent = 'Tillagd i kundvagnen';
        },
        onDone: () => {
          if (label) label.textContent = original;
        }
      });
    });
  }

  async function loadRecommendations(section) {
    const url = section.dataset.spfRecsUrl;
    if (!url || section.dataset.spfRecsLoaded) return;
    section.dataset.spfRecsLoaded = 'true';
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-spf-recs]');
      const target = section.querySelector('[data-spf-recs]');
      if (!fresh || !target || !fresh.querySelector('.spf-card')) return;
      target.replaceChildren(...fresh.childNodes);
      section.hidden = false;
    } catch (error) {
      /* Recommendations are optional; the section stays hidden. */
    }
  }

  function init(scope) {
    scope.querySelectorAll('[data-spf-product]').forEach((root) => {
      if (!root.spfProduct) root.spfProduct = new SpfProduct(root);
    });
    scope.querySelectorAll('[data-spf-recs-url]').forEach(loadRecommendations);
  }

  initCardForms();
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
